import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/solid';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatAmount } from 'utils/formatters/formatAmount';

import { useGetDisbursementPayoutRequestsUniqueReference } from 'api';

import { useCurrentOrganisation } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { DetailsPayoutAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsPayoutAttachmentSectionContainer';
import { SuccessCheckmark } from '@atoms/illustrations/SuccessCheckmark';
import { Spinner } from '@atoms/Spinner/Spinner';
import { StatusTag } from '@atoms/StatusTag';

import type { SubmittedPayout } from './utils/types';

import { PAYOUT_STATUSES } from '../constants';

type ManualPayoutSubmittedProps = {
  submittedPayout: Extract<SubmittedPayout, { state: 'received' }>;
};

export const ManualPayoutSubmitted = ({
  submittedPayout
}: ManualPayoutSubmittedProps) => {
  const organisation = useCurrentOrganisation();

  const { data: { data: payout } = {}, isPending } =
    useGetDisbursementPayoutRequestsUniqueReference(
      organisation.id,
      submittedPayout.uniqueReference
    );

  const navigate = useNavigate();

  if (isPending || payout === undefined) {
    return (
      <div className="flex size-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex size-full justify-center">
      <div className="flex w-104 flex-col items-center gap-4">
        <SuccessCheckmark className="size-24" />
        <div className="w-full text-center">
          <h1 className="text-2xl font-bold">Payout created</h1>
          <h3 className="text-base font-normal text-content-strong">
            Your payout request has been successfully created.
          </h3>
        </div>
        <div className="w-full rounded-2xl border border-border-muted p-5">
          <div className="flex w-full flex-col gap-3">
            <InfoRow
              label="Request ID"
              value={payout.unique_reference.toUpperCase()}
            />
            <InfoRow
              label="Amount"
              value={formatAmount({
                amount: payout.amount,
                currency: payout.currency
              })}
            />
            {payout.creditor && (
              <InfoRow label="Receiver" value={payout.creditor.full_name} />
            )}
            <InfoRow label="Submitted" value={payout.timestamp} />
            <InfoRow
              label="Status"
              value={
                <StatusTag status={payout.status} statuses={PAYOUT_STATUSES} />
              }
            />
          </div>
          <div className="mt-4">
            <div className="text-sm font-medium">Attach files</div>
            <div className="pb-2 text-xs text-content-strong">
              Add supporting documents (optional).
            </div>
            <DetailsPayoutAttachmentSectionContainer
              resourceId={payout.unique_reference}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <PrimaryButton
            className="flex h-9 items-center border-2 border-border-brand"
            onClick={() => navigate(`/org/${organisation.id}`)}
          >
            <ArrowLeftIcon className="size-5" />
            <span className="align-middle text-sm">Back to dashboard</span>
          </PrimaryButton>
          <PrimaryButton
            className="flex h-9 items-center text-sm"
            onClick={() => navigate(0)}
          >
            <PlusIcon className="size-5" />
            <span className="align-middle text-sm">Create new payout</span>
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

type InfoRowProps = {
  label: ReactNode;
  value: ReactNode;
};

const InfoRow = ({ label, value }: InfoRowProps) => {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-content-secondary">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
};
